import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageOff } from 'lucide-react';
import type { MessageDetail } from '@dts/core';
import { api } from '@/lib/api';
import { renderEmail } from '@/lib/email-html';
import { composeMailto } from '@/lib/compose';
import { useUi } from '@/lib/store';
import { Button } from './ui';

const trustedSenders = new Set<string>();

/** Email content in a sandboxed iframe (no scripts) that grows to fit its content. */
export function MessageBody({ detail }: { detail: MessageDetail }) {
  const theme = useUi((s) => s.local.theme);
  const remotePref = useUi((s) => s.local.remoteImages);
  const dark = theme === 'dark' || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  const sender = detail.summary.fromAddress;
  const [allowRemote, setAllowRemote] = useState(remotePref === 'always' || trustedSenders.has(sender) || detail.summary.direction === 'outbound');
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(80);

  const inlineParts = useMemo(
    () => detail.attachments.map((a, i) => ({ a, index: i + 1 })).filter(({ a }) => a.contentId && detail.htmlBody?.includes(a.contentId.replace(/^<|>$/g, ''))),
    [detail],
  );
  const inline = useQuery({
    queryKey: ['inline', detail.summary.id],
    enabled: inlineParts.length > 0,
    staleTime: Infinity,
    queryFn: async () => {
      const map: Record<string, string> = {};
      await Promise.all(
        inlineParts.map(async ({ a, index }) => {
          try {
            map[a.contentId!.replace(/^<|>$/g, '')] = `data:${a.contentType};base64,${await api.attachmentBase64(detail.summary.id, index)}`;
          } catch {
            /* skip broken part */
          }
        }),
      );
      return map;
    },
  });

  const rendered = useMemo(
    () => renderEmail(detail.htmlBody, detail.textBody, { allowRemote, dark, inline: inline.data ?? {} }),
    [detail.htmlBody, detail.textBody, allowRemote, dark, inline.data],
  );

  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    let ro: ResizeObserver | null = null;
    const measure = () => {
      const doc = el.contentDocument;
      if (!doc?.body) return;
      setHeight(Math.max(40, Math.ceil(doc.documentElement.scrollHeight)));
    };
    const onLoad = () => {
      measure();
      const doc = el.contentDocument;
      if (doc?.body) {
        ro = new ResizeObserver(measure);
        ro.observe(doc.body);
        doc.querySelectorAll('img').forEach((img) => img.addEventListener('load', measure));
        // mailto: links open our composer rather than whatever mail program the browser has registered.
        doc.addEventListener('click', (e) => {
          const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
          if (a?.href.toLowerCase().startsWith('mailto:')) {
            e.preventDefault();
            composeMailto(a.href);
          }
        });
      }
    };
    el.addEventListener('load', onLoad);
    return () => {
      el.removeEventListener('load', onLoad);
      ro?.disconnect();
    };
  }, [rendered.srcdoc]);

  return (
    <div>
      {rendered.blockedImages > 0 && !allowRemote && (
        <div className="mb-3 flex items-center gap-3 rounded-xl border border-line bg-line/50 px-3 py-2 text-[12.5px] text-muted">
          <ImageOff className="size-4 shrink-0" />
          <span className="flex-1">Remote images blocked to protect your privacy (they can track when you open mail).</span>
          <Button size="sm" variant="ghost" onClick={() => setAllowRemote(true)}>
            Load images
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              trustedSenders.add(sender);
              setAllowRemote(true);
            }}
          >
            Always for sender
          </Button>
        </div>
      )}
      <iframe
        ref={frame}
        title="Email content"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={rendered.srcdoc}
        className="block w-full border-0 bg-transparent"
        style={{ height, colorScheme: 'normal' }}
      />
    </div>
  );
}

/** Prints one message through a temporary, script-free iframe. */
export function printMessage(detail: MessageDetail) {
  const { srcdoc } = renderEmail(detail.htmlBody, detail.textBody, { allowRemote: true, dark: false, inline: {} });
  const header = `<div style="font:13px sans-serif;border-bottom:1px solid #ddd;padding-bottom:10px;margin-bottom:14px">
    <div style="font-size:18px;font-weight:600;margin-bottom:6px">${escape(detail.summary.subject)}</div>
    <div><b>From:</b> ${escape(detail.summary.from)}</div><div><b>To:</b> ${escape(detail.summary.to)}</div>
    ${detail.summary.cc ? `<div><b>Cc:</b> ${escape(detail.summary.cc)}</div>` : ''}
    <div><b>Date:</b> ${new Date(detail.summary.sentDate ?? detail.summary.receivedUtc).toLocaleString()}</div></div>`;
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0';
  iframe.srcdoc = srcdoc.replace('<body>', `<body>${header}`);
  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => iframe.remove(), 1000);
  };
  document.body.appendChild(iframe);
}

function escape(s: string) {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}
